import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useCallback, useRef, useEffect } from "react";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";
import type { paths } from "../../api/generated/api-types";
import type { SiteFilterParams } from "./useSiteList";
import { logger } from "../../observability/LoggerFactory";

type VerifyRequest =
  paths["/sites/verify"]["post"]["requestBody"]["content"]["application/json"];

type VerifyResponse =
  paths["/sites/verify"]["post"]["responses"]["200"]["content"]["application/json"];

type ActiveVerificationResponse =
  paths["/sites/verify/active"]["get"]["responses"]["200"]["content"]["application/json"];

type DeactivateRequest =
  paths["/sites/deactivate"]["post"]["requestBody"]["content"]["application/json"];

type DeactivateResponse =
  paths["/sites/deactivate"]["post"]["responses"]["200"]["content"]["application/json"];

type CollectRequest =
  paths["/sites/params/collect"]["post"]["requestBody"]["content"]["application/json"];

type CollectResponse =
  paths["/sites/params/collect"]["post"]["responses"]["201"]["content"]["application/json"];

type ActiveCollectionResponse =
  paths["/sites/params/collection/active"]["get"]["responses"]["200"]["content"]["application/json"];

export type CollectionSelection = CollectRequest["selection"];

// The verification selection kinds that carry no explicit target (neither a
// credential list nor a filter payload). Derived from the generated contract so
// a new backend selection value cannot silently fall out of sync with the UI.
type UntargetedVerifySelection = Exclude<VerifyRequest["selection"], "selected" | "filtered">;

export type VerificationInput =
  | { selection: UntargetedVerifySelection }
  | { selection: "selected"; credentialIds: string[] }
  | { selection: "filtered"; filters: SiteFilterParams };

export type DeactivationInput =
  | { selection: "selected"; siteIds: string[] }
  | { selection: "filtered"; filters: SiteFilterParams };

function buildVerifyBody(input: VerificationInput): VerifyRequest {
  let verifyBody: VerifyRequest;
  if (input.selection === "selected") {
    verifyBody = { selection: "selected", credentialIds: input.credentialIds };
  } else {
    if (input.selection === "filtered") {
      verifyBody = { selection: "filtered", credentialIds: [], ...input.filters };
    } else {
      verifyBody = { selection: input.selection, credentialIds: [] };
    }
  }
  return verifyBody;
}

function buildDeactivateBody(input: DeactivationInput): DeactivateRequest {
  let deactivateBody: DeactivateRequest;
  if (input.selection === "selected") {
    deactivateBody = { selection: "selected", siteIds: input.siteIds };
  } else {
    deactivateBody = { selection: "filtered", siteIds: [], ...input.filters };
  }
  return deactivateBody;
}

// A gateway (e.g. nginx) can answer a failed request with an HTML error page
// instead of the backend's JSON error body; parsing that as JSON throws a
// confusing "Unexpected token '<'" instead of a usable message. Only parse
// the body as JSON when the response declares a JSON content type; otherwise
// fall back to a plain HTTP-status message.
async function readErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get("content-type");
  const isJsonErrorBody = contentType !== null && contentType.includes("application/json");
  let message = `HTTP ${response.status}`;
  if (isJsonErrorBody) {
    const errorBody = (await response.json()) as { error: string };
    message = errorBody.error;
  }
  return message;
}

export interface CredentialCheckedEvent {
  credentialId: string;
  siteId: string;
  domain: string;
  errorCode: string | null;
  errorDescription: string | null;
  articleXmlRpcStatus: "unchecked" | "valid" | "invalid";
  articleXmlRpcErrorCode: string | null;
  articleXmlRpcErrorDescription: string | null;
  articleApiStatus: "unchecked" | "valid" | "invalid";
  articleApiErrorCode: string | null;
  articleApiErrorDescription: string | null;
  articleWpAdminStatus: "unchecked" | "valid" | "invalid";
  articleWpAdminErrorCode: string | null;
  articleWpAdminErrorDescription: string | null;
  homepageApiStatus: "unchecked" | "valid" | "invalid";
  homepageApiErrorCode: string | null;
  homepageApiErrorDescription: string | null;
  homepageWpAdminStatus: "unchecked" | "valid" | "invalid";
  homepageWpAdminErrorCode: string | null;
  homepageWpAdminErrorDescription: string | null;
  linkRemovalApiStatus: "unchecked" | "valid" | "invalid";
  linkRemovalApiErrorCode: string | null;
  linkRemovalApiErrorDescription: string | null;
  linkRemovalXmlRpcStatus: "unchecked" | "valid" | "invalid";
  linkRemovalXmlRpcErrorCode: string | null;
  linkRemovalXmlRpcErrorDescription: string | null;
  linkRemovalWpAdminStatus: "unchecked" | "valid" | "invalid";
  linkRemovalWpAdminErrorCode: string | null;
  linkRemovalWpAdminErrorDescription: string | null;
}

export interface SitePluginCheckedEvent {
  siteId: string;
  siteUrl: string;
  pluginHomepageStatus: "valid" | "invalid";
  homepageLastCheckedAt: string;
  homepageErrorCode: string | null;
  homepageErrorDescription: string | null;
  pluginArticleStatus: "valid" | "invalid";
  articleLastCheckedAt: string;
  articleErrorCode: string | null;
  articleErrorDescription: string | null;
  pluginLinkRemovalStatus: "valid" | "invalid";
  linkRemovalLastCheckedAt: string;
  linkRemovalErrorCode: string | null;
  linkRemovalErrorDescription: string | null;
}

export interface VerificationSummary {
  totalCredentials: number;
  checkedCount: number;
  articleValidCount: number;
  articleXmlRpcValidCount: number;
  articleApiValidCount: number;
  articleWpAdminValidCount: number;
  articleInvalidCount: number;
  homepageValidCount: number;
  homepageApiValidCount: number;
  homepageWpAdminValidCount: number;
  homepageInvalidCount: number;
  linkRemovalValidCount: number;
  linkRemovalApiValidCount: number;
  linkRemovalXmlRpcValidCount: number;
  linkRemovalInvalidCount: number;
  totalSites: number;
  sitesPluginCheckedCount: number;
  sitesPluginHomepageValidCount: number;
  sitesPluginHomepageInvalidCount: number;
  sitesPluginArticleValidCount: number;
  sitesPluginArticleInvalidCount: number;
  sitesPluginSitewideValidCount: number;
  sitesPluginSitewideInvalidCount: number;
  sitesPluginLinkRemovalValidCount: number;
  sitesPluginLinkRemovalInvalidCount: number;
  sitesArticleValidCount: number;
  sitesArticleInvalidCount: number;
  sitesHomepageValidCount: number;
  sitesHomepageInvalidCount: number;
  sitesLinkRemovalValidCount: number;
  sitesLinkRemovalInvalidCount: number;
}

interface VerificationSnapshotPayload {
  totalSites: number;
  sitesArticleValidCount: number;
  sitesArticleInvalidCount: number;
  sitesHomepageValidCount: number;
  sitesHomepageInvalidCount: number;
  sitesLinkRemovalValidCount: number;
  sitesLinkRemovalInvalidCount: number;
  sitesPluginCheckedCount: number;
  sitesPluginHomepageValidCount: number;
  sitesPluginHomepageInvalidCount: number;
  sitesPluginArticleValidCount: number;
  sitesPluginArticleInvalidCount: number;
  sitesPluginSitewideValidCount: number;
  sitesPluginSitewideInvalidCount: number;
  sitesPluginLinkRemovalValidCount: number;
  sitesPluginLinkRemovalInvalidCount: number;
}

export interface CollectionSummary {
  totalSites: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
}

type VerificationState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "progress"; verificationId: string; summary: VerificationSummary }
  | { kind: "completed"; summary: VerificationSummary }
  | { kind: "error"; message: string };

type DeactivationState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; deactivatedCount: number }
  | { kind: "error"; message: string };

type CollectionState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "progress"; collectionId: string; totalSites: number; processedCount: number; successCount: number; failedCount: number }
  | { kind: "completed"; successCount: number; failedCount: number }
  | { kind: "error"; message: string };

export interface UseSiteListActionsResult {
  verification: VerificationState;
  deactivation: DeactivationState;
  collection: CollectionState;
  startVerification: (input: VerificationInput) => void;
  cancelVerification: () => void;
  cancelCollection: () => void;
  deactivateSites: (input: DeactivationInput) => void;
  resetVerification: () => void;
  resetDeactivation: () => void;
  startCollection: (selection: CollectionSelection, siteIds: string[]) => void;
  resetCollection: () => void;
}

export function useSiteListActions(onRefetch: () => void): UseSiteListActionsResult {
  const [verification, setVerification] = useState<VerificationState>({ kind: "idle" });
  const [deactivation, setDeactivation] = useState<DeactivationState>({ kind: "idle" });
  const [collection, setCollection] = useState<CollectionState>({ kind: "idle" });
  const sseClientRef = useRef<SseClient | null>(null);
  const collectionSseClientRef = useRef<SseClient | null>(null);
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;
  const verificationRef = useRef<VerificationState>(verification);
  verificationRef.current = verification;
  const collectionRef = useRef<CollectionState>(collection);
  collectionRef.current = collection;

  useEffect(() => {
    return () => {
      if (sseClientRef.current !== null) {
        sseClientRef.current.close();
      }
      if (collectionSseClientRef.current !== null) {
        collectionSseClientRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resume() {
      try {
        const response = await fetchWithRefresh("/api/sites/verify/active");
        if (!response.ok || cancelled) {return;}
        const data = (await response.json()) as ActiveVerificationResponse;
        if (data === null || cancelled) {return;}

        setVerification({
          kind: "progress",
          verificationId: data.verificationId,
          summary: {
            totalCredentials: data.totalCredentials,
            checkedCount: data.checkedCount,
            articleValidCount: data.articleValidCount,
            articleXmlRpcValidCount: data.articleXmlRpcValidCount,
            articleApiValidCount: data.articleApiValidCount,
            articleWpAdminValidCount: data.articleWpAdminValidCount,
            articleInvalidCount: data.articleInvalidCount,
            homepageValidCount: data.homepageValidCount,
            homepageApiValidCount: data.homepageApiValidCount,
            homepageWpAdminValidCount: data.homepageWpAdminValidCount,
            homepageInvalidCount: data.homepageInvalidCount,
            linkRemovalValidCount: data.linkRemovalValidCount,
            linkRemovalApiValidCount: data.linkRemovalApiValidCount,
            linkRemovalXmlRpcValidCount: data.linkRemovalXmlRpcValidCount,
            linkRemovalInvalidCount: data.linkRemovalInvalidCount,
            totalSites: data.totalSites,
            sitesPluginCheckedCount: data.sitesPluginCheckedCount,
            sitesPluginHomepageValidCount: data.sitesPluginHomepageValidCount,
            sitesPluginHomepageInvalidCount: data.sitesPluginHomepageInvalidCount,
            sitesPluginArticleValidCount: data.sitesPluginArticleValidCount,
            sitesPluginArticleInvalidCount: data.sitesPluginArticleInvalidCount,
            sitesPluginSitewideValidCount: data.sitesPluginSitewideValidCount,
            sitesPluginSitewideInvalidCount: data.sitesPluginSitewideInvalidCount,
            sitesPluginLinkRemovalValidCount: data.sitesPluginLinkRemovalValidCount,
            sitesPluginLinkRemovalInvalidCount: data.sitesPluginLinkRemovalInvalidCount,
            sitesArticleValidCount: data.sitesArticleValidCount,
            sitesArticleInvalidCount: data.sitesArticleInvalidCount,
            sitesHomepageValidCount: data.sitesHomepageValidCount,
            sitesHomepageInvalidCount: data.sitesHomepageInvalidCount,
            sitesLinkRemovalValidCount: data.sitesLinkRemovalValidCount,
            sitesLinkRemovalInvalidCount: data.sitesLinkRemovalInvalidCount,
          },
        });
        connectSse(data.verificationId);
      } catch (error: unknown) {
        // Восстановление подписки — попытка без гарантий: экран остаётся в
        // исходном состоянии, а причина уходит в журнал.
        logger.warn({ err: error, component: "useSiteListActions" }, "Failed to resume site verification");
      }
    }

    void resume();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resumeCollection() {
      try {
        const response = await fetchWithRefresh("/api/sites/params/collection/active");
        if (!response.ok || cancelled) {return;}
        const data = (await response.json()) as ActiveCollectionResponse;
        if (data === null || cancelled) {return;}

        setCollection({
          kind: "progress",
          collectionId: data.collectionId,
          totalSites: data.totalSites,
          processedCount: data.processedCount,
          successCount: data.successCount,
          failedCount: data.failedCount,
        });
        connectCollectionSse(data.collectionId);
      } catch (error: unknown) {
        // Восстановление подписки — попытка без гарантий: экран остаётся в
        // исходном состоянии, а причина уходит в журнал.
        logger.warn({ err: error, component: "useSiteListActions" }, "Failed to resume params collection");
      }
    }

    void resumeCollection();
    return () => {
      cancelled = true;
    };
  }, []);

  function connectSse(verificationId: string) {
    if (sseClientRef.current !== null) {
      sseClientRef.current.close();
    }
    const client = createSseClient({
      url: `/api/sites/verify/${verificationId}/subscribe`,
      events: {
        "verification:snapshot"(raw: unknown) {
          const seconds = raw as {
            totalCredentials: number;
            checkedCount: number;
            articleValidCount: number;
            articleXmlRpcValidCount: number;
            articleApiValidCount: number;
            articleWpAdminValidCount: number;
            articleInvalidCount: number;
            homepageValidCount: number;
            homepageApiValidCount: number;
            homepageWpAdminValidCount: number;
            homepageInvalidCount: number;
            linkRemovalValidCount: number;
            linkRemovalApiValidCount: number;
            linkRemovalXmlRpcValidCount: number;
            linkRemovalInvalidCount: number;
          } & VerificationSnapshotPayload;
          setVerification({
            kind: "progress",
            verificationId,
            summary: {
              // Planned until the fan-out fixes the factual volume, so the snapshot carries the
              // current denominator rather than the one captured when the run started.
              totalCredentials: seconds.totalCredentials,
              checkedCount: seconds.checkedCount,
              articleValidCount: seconds.articleValidCount,
              articleXmlRpcValidCount: seconds.articleXmlRpcValidCount,
              articleApiValidCount: seconds.articleApiValidCount,
              articleWpAdminValidCount: seconds.articleWpAdminValidCount,
              articleInvalidCount: seconds.articleInvalidCount,
              homepageValidCount: seconds.homepageValidCount,
              homepageApiValidCount: seconds.homepageApiValidCount,
              homepageWpAdminValidCount: seconds.homepageWpAdminValidCount,
              homepageInvalidCount: seconds.homepageInvalidCount,
              linkRemovalValidCount: seconds.linkRemovalValidCount,
              linkRemovalApiValidCount: seconds.linkRemovalApiValidCount,
              linkRemovalXmlRpcValidCount: seconds.linkRemovalXmlRpcValidCount,
              linkRemovalInvalidCount: seconds.linkRemovalInvalidCount,
              totalSites: seconds.totalSites,
              sitesPluginCheckedCount: seconds.sitesPluginCheckedCount,
              sitesPluginHomepageValidCount: seconds.sitesPluginHomepageValidCount,
              sitesPluginHomepageInvalidCount: seconds.sitesPluginHomepageInvalidCount,
              sitesPluginArticleValidCount: seconds.sitesPluginArticleValidCount,
              sitesPluginArticleInvalidCount: seconds.sitesPluginArticleInvalidCount,
              sitesPluginSitewideValidCount: seconds.sitesPluginSitewideValidCount,
              sitesPluginSitewideInvalidCount: seconds.sitesPluginSitewideInvalidCount,
              sitesPluginLinkRemovalValidCount: seconds.sitesPluginLinkRemovalValidCount,
              sitesPluginLinkRemovalInvalidCount: seconds.sitesPluginLinkRemovalInvalidCount,
              sitesArticleValidCount: seconds.sitesArticleValidCount,
              sitesArticleInvalidCount: seconds.sitesArticleInvalidCount,
              sitesHomepageValidCount: seconds.sitesHomepageValidCount,
              sitesHomepageInvalidCount: seconds.sitesHomepageInvalidCount,
              sitesLinkRemovalValidCount: seconds.sitesLinkRemovalValidCount,
              sitesLinkRemovalInvalidCount: seconds.sitesLinkRemovalInvalidCount,
            },
          });
        },
        "credential:checked"(raw: unknown) {
          const event = raw as CredentialCheckedEvent & { snapshot: VerificationSnapshotPayload };
          setVerification((previous) => {
            let next = previous;
            if (previous.kind === "progress") {
              const updated: VerificationSummary = {
                ...previous.summary,
                checkedCount: previous.summary.checkedCount + 1,
                totalSites: event.snapshot.totalSites,
                sitesArticleValidCount: event.snapshot.sitesArticleValidCount,
                sitesArticleInvalidCount: event.snapshot.sitesArticleInvalidCount,
                sitesHomepageValidCount: event.snapshot.sitesHomepageValidCount,
                sitesHomepageInvalidCount: event.snapshot.sitesHomepageInvalidCount,
                sitesLinkRemovalValidCount: event.snapshot.sitesLinkRemovalValidCount,
                sitesLinkRemovalInvalidCount: event.snapshot.sitesLinkRemovalInvalidCount,
                sitesPluginCheckedCount: event.snapshot.sitesPluginCheckedCount,
                sitesPluginHomepageValidCount: event.snapshot.sitesPluginHomepageValidCount,
                sitesPluginHomepageInvalidCount: event.snapshot.sitesPluginHomepageInvalidCount,
              };
              const articleXmlRpcValid = event.articleXmlRpcStatus === "valid";
              const articleApiValid = event.articleApiStatus === "valid";
              const articleWpAdminValid = event.articleWpAdminStatus === "valid";
              if (articleXmlRpcValid) {
                updated.articleXmlRpcValidCount = previous.summary.articleXmlRpcValidCount + 1;
              }
              if (articleApiValid) {
                updated.articleApiValidCount = previous.summary.articleApiValidCount + 1;
              }
              const articleReachable = articleXmlRpcValid || articleApiValid || articleWpAdminValid;
              const articleUnreachable =
                event.articleXmlRpcStatus === "invalid" &&
                event.articleApiStatus === "invalid" &&
                event.articleWpAdminStatus === "invalid";
              if (articleReachable) {
                updated.articleValidCount = previous.summary.articleValidCount + 1;
              } else if (articleUnreachable) {
                updated.articleInvalidCount = previous.summary.articleInvalidCount + 1;
              }
              const homepageApiValid = event.homepageApiStatus === "valid";
              const homepageWpAdminValid = event.homepageWpAdminStatus === "valid";
              if (homepageApiValid) {
                updated.homepageApiValidCount = previous.summary.homepageApiValidCount + 1;
              }
              if (homepageWpAdminValid) {
                updated.homepageWpAdminValidCount = previous.summary.homepageWpAdminValidCount + 1;
              }
              if (homepageApiValid || homepageWpAdminValid) {
                updated.homepageValidCount = previous.summary.homepageValidCount + 1;
              } else if (
                event.homepageApiStatus === "invalid" &&
                event.homepageWpAdminStatus === "invalid"
              ) {
                updated.homepageInvalidCount = previous.summary.homepageInvalidCount + 1;
              }
              const linkRemovalApiValid = event.linkRemovalApiStatus === "valid";
              const linkRemovalXmlRpcValid = event.linkRemovalXmlRpcStatus === "valid";
              const linkRemovalWpAdminValid = event.linkRemovalWpAdminStatus === "valid";
              if (linkRemovalApiValid) {
                updated.linkRemovalApiValidCount = previous.summary.linkRemovalApiValidCount + 1;
              }
              if (linkRemovalXmlRpcValid) {
                updated.linkRemovalXmlRpcValidCount = previous.summary.linkRemovalXmlRpcValidCount + 1;
              }
              const linkRemovalReachable =
                linkRemovalApiValid || linkRemovalXmlRpcValid || linkRemovalWpAdminValid;
              const linkRemovalUnreachable =
                event.linkRemovalApiStatus === "invalid" &&
                event.linkRemovalXmlRpcStatus === "invalid" &&
                event.linkRemovalWpAdminStatus === "invalid";
              if (linkRemovalReachable) {
                updated.linkRemovalValidCount = previous.summary.linkRemovalValidCount + 1;
              } else if (linkRemovalUnreachable) {
                updated.linkRemovalInvalidCount = previous.summary.linkRemovalInvalidCount + 1;
              }
              next = { ...previous, summary: updated };
            }
            return next;
          });
        },
        "site-plugin:checked"(raw: unknown) {
          const event = raw as SitePluginCheckedEvent & { snapshot: VerificationSnapshotPayload };
          setVerification((previous) => {
            let next = previous;
            if (previous.kind === "progress") {
              const updated: VerificationSummary = {
                ...previous.summary,
                totalSites: event.snapshot.totalSites,
                sitesArticleValidCount: event.snapshot.sitesArticleValidCount,
                sitesArticleInvalidCount: event.snapshot.sitesArticleInvalidCount,
                sitesHomepageValidCount: event.snapshot.sitesHomepageValidCount,
                sitesHomepageInvalidCount: event.snapshot.sitesHomepageInvalidCount,
                sitesLinkRemovalValidCount: event.snapshot.sitesLinkRemovalValidCount,
                sitesLinkRemovalInvalidCount: event.snapshot.sitesLinkRemovalInvalidCount,
                sitesPluginCheckedCount: event.snapshot.sitesPluginCheckedCount,
                sitesPluginHomepageValidCount: event.snapshot.sitesPluginHomepageValidCount,
                sitesPluginHomepageInvalidCount: event.snapshot.sitesPluginHomepageInvalidCount,
                sitesPluginArticleValidCount: event.snapshot.sitesPluginArticleValidCount,
                sitesPluginArticleInvalidCount: event.snapshot.sitesPluginArticleInvalidCount,
                sitesPluginSitewideValidCount: event.snapshot.sitesPluginSitewideValidCount,
                sitesPluginSitewideInvalidCount: event.snapshot.sitesPluginSitewideInvalidCount,
                sitesPluginLinkRemovalValidCount: event.snapshot.sitesPluginLinkRemovalValidCount,
                sitesPluginLinkRemovalInvalidCount: event.snapshot.sitesPluginLinkRemovalInvalidCount,
              };
              next = { ...previous, summary: updated };
            }
            return next;
          });
        },
        "verification:completed"(raw: unknown) {
          const completed = raw as {
            totalCredentials: number;
            checkedCount: number;
            articleValidCount: number;
            articleXmlRpcValidCount: number;
            articleApiValidCount: number;
            articleWpAdminValidCount: number;
            articleInvalidCount: number;
            homepageValidCount: number;
            homepageApiValidCount: number;
            homepageWpAdminValidCount: number;
            homepageInvalidCount: number;
            linkRemovalValidCount: number;
            linkRemovalApiValidCount: number;
            linkRemovalXmlRpcValidCount: number;
            linkRemovalInvalidCount: number;
          } & VerificationSnapshotPayload;
          setVerification({
            kind: "completed",
            summary: {
              totalCredentials: completed.totalCredentials,
              checkedCount: completed.checkedCount,
              articleValidCount: completed.articleValidCount,
              articleXmlRpcValidCount: completed.articleXmlRpcValidCount,
              articleApiValidCount: completed.articleApiValidCount,
              articleWpAdminValidCount: completed.articleWpAdminValidCount,
              articleInvalidCount: completed.articleInvalidCount,
              homepageValidCount: completed.homepageValidCount,
              homepageApiValidCount: completed.homepageApiValidCount,
              homepageWpAdminValidCount: completed.homepageWpAdminValidCount,
              homepageInvalidCount: completed.homepageInvalidCount,
              linkRemovalValidCount: completed.linkRemovalValidCount,
              linkRemovalApiValidCount: completed.linkRemovalApiValidCount,
              linkRemovalXmlRpcValidCount: completed.linkRemovalXmlRpcValidCount,
              linkRemovalInvalidCount: completed.linkRemovalInvalidCount,
              totalSites: completed.totalSites,
              sitesPluginCheckedCount: completed.sitesPluginCheckedCount,
              sitesPluginHomepageValidCount: completed.sitesPluginHomepageValidCount,
              sitesPluginHomepageInvalidCount: completed.sitesPluginHomepageInvalidCount,
              sitesPluginArticleValidCount: completed.sitesPluginArticleValidCount,
              sitesPluginArticleInvalidCount: completed.sitesPluginArticleInvalidCount,
              sitesPluginSitewideValidCount: completed.sitesPluginSitewideValidCount,
              sitesPluginSitewideInvalidCount: completed.sitesPluginSitewideInvalidCount,
              sitesPluginLinkRemovalValidCount: completed.sitesPluginLinkRemovalValidCount,
              sitesPluginLinkRemovalInvalidCount: completed.sitesPluginLinkRemovalInvalidCount,
              sitesArticleValidCount: completed.sitesArticleValidCount,
              sitesArticleInvalidCount: completed.sitesArticleInvalidCount,
              sitesHomepageValidCount: completed.sitesHomepageValidCount,
              sitesHomepageInvalidCount: completed.sitesHomepageInvalidCount,
              sitesLinkRemovalValidCount: completed.sitesLinkRemovalValidCount,
              sitesLinkRemovalInvalidCount: completed.sitesLinkRemovalInvalidCount,
            },
          });
          if (sseClientRef.current !== null) {
            sseClientRef.current.close();
          }
          sseClientRef.current = null;
          onRefetchRef.current();
        },
      },
      onError() {
        setVerification({ kind: "error", message: "Потеряно соединение с сервером" });
      },
    });
    sseClientRef.current = client;
  }

  function connectCollectionSse(collectionId: string) {
    if (collectionSseClientRef.current !== null) {
      collectionSseClientRef.current.close();
    }
    const client = createSseClient({
      url: `/api/sites/params/collection/${collectionId}/subscribe`,
      events: {
        "collection:snapshot"(raw: unknown) {
          const seconds = raw as { processedCount: number; successCount: number; failedCount: number };
          setCollection((previous) => {
            let next = previous;
            if (previous.kind === "progress") {
              next = { ...previous, processedCount: seconds.processedCount, successCount: seconds.successCount, failedCount: seconds.failedCount };
            }
            return next;
          });
        },
        "site:params_collected"(raw: unknown) {
          const event = raw as { siteId: string; success: boolean };
          setCollection((previous) => {
            let next = previous;
            if (previous.kind === "progress") {
              next = {
                ...previous,
                processedCount: previous.processedCount + 1,
                successCount: previous.successCount + (event.success ? 1 : 0),
                failedCount: previous.failedCount + (event.success ? 0 : 1),
              };
            }
            return next;
          });
        },
        "collection:completed"(raw: unknown) {
          const completed = raw as { totalSites: number; successCount: number; failedCount: number };
          setCollection({
            kind: "completed",
            successCount: completed.successCount,
            failedCount: completed.failedCount,
          });
          if (collectionSseClientRef.current !== null) {
            collectionSseClientRef.current.close();
          }
          collectionSseClientRef.current = null;
          onRefetchRef.current();
        },
      },
      onError() {
        setCollection({ kind: "error", message: "Потеряно соединение с сервером" });
      },
    });
    collectionSseClientRef.current = client;
  }

  const startVerification = useCallback(
    (input: VerificationInput) => {
      void (async () => {
        setVerification({ kind: "starting" });

        try {
          const body: VerifyRequest = buildVerifyBody(input);
          const response = await fetchWithRefresh("/api/sites/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(await readErrorMessage(response));
          }
          const data = (await response.json()) as VerifyResponse;

          if (data.totalCredentials === 0) {
            setVerification({
              kind: "completed",
              summary: {
                totalCredentials: 0,
                checkedCount: 0,
                articleValidCount: 0,
                articleXmlRpcValidCount: 0,
                articleApiValidCount: 0,
                articleWpAdminValidCount: 0,
                articleInvalidCount: 0,
                homepageValidCount: 0,
                homepageInvalidCount: 0,
                homepageApiValidCount: 0,
                homepageWpAdminValidCount: 0,
                linkRemovalValidCount: 0,
                linkRemovalApiValidCount: 0,
                linkRemovalXmlRpcValidCount: 0,
                linkRemovalInvalidCount: 0,
                totalSites: 0,
                sitesPluginCheckedCount: 0,
                sitesPluginHomepageValidCount: 0,
                sitesPluginHomepageInvalidCount: 0,
                sitesPluginArticleValidCount: 0,
                sitesPluginArticleInvalidCount: 0,
                sitesPluginSitewideValidCount: 0,
                sitesPluginSitewideInvalidCount: 0,
                sitesPluginLinkRemovalValidCount: 0,
                sitesPluginLinkRemovalInvalidCount: 0,
                sitesArticleValidCount: 0,
                sitesArticleInvalidCount: 0,
                sitesHomepageValidCount: 0,
                sitesHomepageInvalidCount: 0,
                sitesLinkRemovalValidCount: 0,
                sitesLinkRemovalInvalidCount: 0,
              },
            });
            return;
          }

          connectSse(data.verificationId);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to start verification");
          setVerification({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const deactivateSites = useCallback(
    (input: DeactivationInput) => {
      void (async () => {
        setDeactivation({ kind: "loading" });

        try {
          const body: DeactivateRequest = buildDeactivateBody(input);
          const response = await fetchWithRefresh("/api/sites/deactivate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = (await response.json()) as DeactivateResponse;
          setDeactivation({ kind: "success", deactivatedCount: data.deactivatedCount });
          onRefetchRef.current();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to deactivate sites");
          setDeactivation({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const startCollection = useCallback(
    (selection: CollectionSelection, siteIds: string[]) => {
      void (async () => {
        setCollection({ kind: "starting" });

        try {
          const body: CollectRequest = { selection, siteIds };
          const response = await fetchWithRefresh("/api/sites/params/collect", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(await readErrorMessage(response));
          }
          const data = (await response.json()) as CollectResponse;

          setCollection({
            kind: "progress",
            collectionId: data.collectionId,
            totalSites: data.totalSites,
            processedCount: 0,
            successCount: 0,
            failedCount: 0,
          });
          connectCollectionSse(data.collectionId);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to start collection");
          setCollection({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const cancelVerification = useCallback(() => {
    void (async () => {
      const current = verificationRef.current;
      if (current.kind !== "progress") {
        return;
      }
      try {
        const response = await fetchWithRefresh(`/api/sites/verify/${current.verificationId}/cancel`, {
          method: "POST",
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to cancel verification");
      }
    })();
  }, []);

  const cancelCollection = useCallback(() => {
    void (async () => {
      const current = collectionRef.current;
      if (current.kind !== "progress") {
        return;
      }
      try {
        const response = await fetchWithRefresh(`/api/sites/params/${current.collectionId}/cancel`, {
          method: "POST",
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to cancel collection");
      }
    })();
  }, []);

  const resetVerification = useCallback(() => {
    if (sseClientRef.current !== null) {
      sseClientRef.current.close();
    }
    sseClientRef.current = null;
    setVerification({ kind: "idle" });
  }, []);

  const resetDeactivation = useCallback(() => {
    setDeactivation({ kind: "idle" });
  }, []);

  const resetCollection = useCallback(() => {
    if (collectionSseClientRef.current !== null) {
      collectionSseClientRef.current.close();
    }
    collectionSseClientRef.current = null;
    setCollection({ kind: "idle" });
  }, []);

  return {
    verification,
    deactivation,
    collection,
    startVerification,
    cancelVerification,
    cancelCollection,
    deactivateSites,
    resetVerification,
    resetDeactivation,
    startCollection,
    resetCollection,
  };
}

